import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Modal from './common/Modal';
import { addDays } from 'date-fns';

const FiSave = FiIcons.FiSave;
const FiX = FiIcons.FiX;
const FiCalendar = FiIcons.FiCalendar;
const FiFlag = FiIcons.FiFlag;
const FiType = FiIcons.FiType;
const FiFileText = FiIcons.FiFileText;
const FiFolders = FiIcons.FiFolder;
const FiClock = FiIcons.FiClock;
const FiPlus = FiIcons.FiPlus;
const FiMinus = FiIcons.FiMinus;
const FiTag = FiIcons.FiTag;
const FiList = FiIcons.FiList;

const TaskForm = ({
  task,
  tasks = [],
  projects,
  currentProjectId,
  onSubmit,
  onClose,
  tags = [],
  onAddTag = () => {},
  editingSubtaskId = null
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    projectId: currentProjectId || 'default',
    dueDate: '',
    endDate: '',
    isMultiDay: false,
    duration: null,
    startTime: '',
    subTasks: [],
    tags: [],
    flexMode: false,
    antiOverlap: false
  });

  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [subtaskError, setSubtaskError] = useState('');
  const [newSubtask, setNewSubtask] = useState({
    title: '',
    dueDate: '',
    endDate: '',
    time: '09:00',
    duration: 30,
    completed: false,
    isMultiDay: false
  });
  const [editingSubId, setEditingSubId] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [customStart, setCustomStart] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        projectId: task.projectId || currentProjectId || 'default',
        dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
        endDate: task.endDate ? task.endDate.split('T')[0] : '',
        isMultiDay: task.isMultiDay || false,
        duration: task.duration ?? null,
        startTime: task.startTime ?? '',
        subTasks: task.subTasks || [],
        tags: task.tags || [],
        flexMode: task.flexMode || false,
        antiOverlap: task.antiOverlap || false
      });
    } else {
      // For new tasks, set the current project ID
      setFormData(prev => ({
        ...prev,
        projectId: currentProjectId || 'default'
      }));
    }
  }, [task, currentProjectId]);

  useEffect(() => {
    if (editingSubtaskId && task) {
      const st = task.subTasks?.find(s => s.id === editingSubtaskId);
      if (st) {
        setNewSubtask({
          title: st.title,
          dueDate: st.dueDate || '',
          endDate: st.endDate || '',
          time: st.time || '09:00',
          duration: st.duration || 30,
          completed: st.completed || false,
          isMultiDay: st.isMultiDay || false
        });
        setShowSubtaskForm(true);
        setEditingSubId(editingSubtaskId);
      }
    }
  }, [editingSubtaskId, task]);

  useEffect(() => {
    if (!formData.antiOverlap || !formData.dueDate || !formData.duration) {
      setAvailableSlots([]);
      return;
    }

    const duration = formData.duration;
    const selectedDate = formData.dueDate;

    const sameDayTasks = tasks.filter(t =>
      t.id !== task?.id &&
      !t.flexMode &&
      !t.isMultiDay &&
      t.dueDate &&
      t.dueDate.split('T')[0] === selectedDate &&
      t.startTime &&
      t.duration
    );

    const busyIntervals = sameDayTasks
      .map(t => {
        const [h, m] = t.startTime.split(':').map(Number);
        return { start: h * 60 + m, end: h * 60 + m + t.duration };
      })
      .sort((a, b) => a.start - b.start);

    const freeIntervals = [];
    let lastEnd = 0;
    busyIntervals.forEach(({ start, end }) => {
      if (start > lastEnd) {
        freeIntervals.push({ start: lastEnd, end: start });
      }
      lastEnd = Math.max(lastEnd, end);
    });
    if (lastEnd < 24 * 60) {
      freeIntervals.push({ start: lastEnd, end: 24 * 60 });
    }

    const toTime = (mins) => {
      const h = String(Math.floor(mins / 60)).padStart(2, '0');
      const m = String(mins % 60).padStart(2, '0');
      return `${h}:${m}`;
    };

    const slotMap = new Map();

    freeIntervals.forEach(({ start, end }) => {
      let current = Math.ceil(start / 30) * 30;
      while (current + duration <= end) {
        const time = toTime(current);
        if (!slotMap.has(time)) slotMap.set(time, { time, highlight: null });
        current += 30;
      }
    });

    sameDayTasks.forEach(t => {
      const [th, tm] = t.startTime.split(':').map(Number);
      const taskStart = th * 60 + tm;
      const taskEnd = taskStart + t.duration;

      // Suggest end-of-task boundary for back-to-back scheduling (green)
      const endInterval = freeIntervals.find(fi => taskEnd >= fi.start && taskEnd + duration <= fi.end);
      if (endInterval) {
        const time = toTime(taskEnd);
        const existing = slotMap.get(time);
        if (existing) {
          existing.highlight = existing.highlight || 'end';
          existing.taskName = existing.taskName || t.title;
        } else {
          slotMap.set(time, { time, highlight: 'end', taskName: t.title });
        }
      }

      // Suggest starting right before an existing task to avoid gaps (orange)
      const candidateStart = taskStart - duration;
      if (candidateStart >= 0) {
        const startInterval = freeIntervals.find(
          fi => candidateStart >= fi.start && candidateStart + duration <= fi.end
        );
        if (startInterval) {
          const time = toTime(candidateStart);
          const existing = slotMap.get(time);
          if (existing) {
            existing.highlight = existing.highlight || 'start';
            existing.taskName = existing.taskName || t.title;
          } else {
            slotMap.set(time, { time, highlight: 'start', taskName: t.title });
          }
        }
      }
    });

    const slots = Array.from(slotMap.values()).sort((a, b) => a.time.localeCompare(b.time));

    setAvailableSlots(slots);
  }, [formData.dueDate, formData.duration, tasks, task, formData.antiOverlap]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = t('validation.titleRequired');
    }

    const now = new Date().setHours(0, 0, 0, 0);
    if (formData.dueDate && new Date(formData.dueDate) < now) {
      newErrors.dueDate = t('validation.dueDatePast');
    }

    if (formData.endDate && new Date(formData.endDate) < now) {
      newErrors.endDate = t('validation.endDatePast');
    }

    if (formData.dueDate && formData.endDate && new Date(formData.endDate) < new Date(formData.dueDate)) {
      newErrors.endDate = t('validation.endDateBeforeStart');
    }

    if (
      formData.antiOverlap &&
      formData.dueDate &&
      formData.startTime &&
      formData.duration
    ) {
      const [h, m] = formData.startTime.split(':').map(Number);
      const start = h * 60 + m;
      const end = start + formData.duration;
      const overlap = tasks.some(tk => {
        if (
          tk.id === task?.id ||
          tk.flexMode ||
          tk.isMultiDay ||
          tk.dueDate?.split('T')[0] !== formData.dueDate ||
          !tk.startTime ||
          !tk.duration
        )
          return false;
        const [th, tm] = tk.startTime.split(':').map(Number);
        const tStart = th * 60 + tm;
        const tEnd = tStart + tk.duration;
        return start < tEnd && end > tStart;
      });
      if (overlap) {
        newErrors.startTime = t('validation.timeOverlap');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const taskData = {
      priority: formData.priority,
      projectId: formData.projectId || 'default',
      title: formData.title.trim(),
      description: formData.description.trim(),
      isMultiDay: formData.isMultiDay,
      endDate: formData.endDate,
      subTasks: formData.subTasks,
      tags: formData.tags,
      flexMode: formData.flexMode,
      antiOverlap: formData.antiOverlap
    };

    if (!formData.flexMode && formData.dueDate) {
      taskData.dueDate = formData.dueDate;
      if (formData.startTime) {
        taskData.startTime = formData.startTime;
      }
      if (formData.duration !== null) {
        taskData.duration = formData.duration;
      }
    }
    if (formData.flexMode) {
      taskData.duration = formData.subTasks.reduce((s, st) => s + (st.duration || 0), 0);
    }

    if (task) {
      onSubmit({ ...task, ...taskData });
    } else {
      onSubmit(taskData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      if (field === 'dueDate') {
        if (!value) {
          updated.startTime = '';
          updated.duration = null;
          updated.endDate = '';
        } else if (prev.isMultiDay && !prev.endDate) {
          const dueDate = new Date(value);
          updated.endDate = addDays(dueDate, 1).toISOString().split('T')[0];
        }
      }

      if (field === 'isMultiDay' && value) {
        if (prev.dueDate && !prev.endDate) {
          const dueDate = new Date(prev.dueDate);
          updated.endDate = addDays(dueDate, 1).toISOString().split('T')[0];
        }
        updated.antiOverlap = false;
      }

      if (field === 'flexMode' && value) {
         updated.dueDate = '';
         updated.startTime = '';
         updated.duration = null;
         updated.endDate = '';
        updated.duration = updated.subTasks.reduce((s, st) => s + (st.duration || 0), 0);
        updated.antiOverlap = false;
      } else if (field === 'flexMode' && !value) {
        updated.subTasks = (updated.subTasks || []).map(st => ({
          ...st,
          dueDate: '',
          time: '',
          endDate: ''
        }));
      }

      if (field === 'duration' && prev.antiOverlap) {
        updated.startTime = '';
        setCustomStart(false);
      }

      if (field === 'antiOverlap') {
        setCustomStart(false);
        if (value) {
          updated.startTime = '';
        }
      }

      return updated;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddTag = async () => {
    if (newTag.trim()) {
      // Check if this tag already exists in the global tags list
      const existingTag = tags.find(t => t.name.toLowerCase() === newTag.toLowerCase());
      
      let tagToAdd;
      if (existingTag) {
        tagToAdd = existingTag.id;
      } else {
        // Create new tag
        const createdTag = await onAddTag({ name: newTag.trim() });
        tagToAdd = createdTag.id;
      }
      
      // Add tag to the task if not already added
      if (!formData.tags.includes(tagToAdd)) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagToAdd]
        }));
      }
      
      setNewTag('');
    }
  };

  const handleToggleTag = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  const handleRemoveTag = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(id => id !== tagId)
    }));
  };

  const handleAddSubtask = () => {
    setSubtaskError('');
    if (!newSubtask.title.trim()) return;

    if (!formData.isMultiDay && !formData.flexMode) {
      const subtask = {
        id: editingSubId || Date.now().toString(),
        title: newSubtask.title,
        duration: newSubtask.duration,
        completed: newSubtask.completed || false
      };

      setFormData(prev => {
        const subs = editingSubId
          ? prev.subTasks.map(st => st.id === editingSubId ? subtask : st)
          : [...prev.subTasks, subtask];
        return {
          ...prev,
          subTasks: subs,
          duration: prev.flexMode ? subs.reduce((s, st) => s + (st.duration || 0), 0) : prev.duration
        };
      });

      setNewSubtask({ title: '', dueDate: '', endDate: '', time: '09:00', duration: 30, completed: false, isMultiDay: false });
      setShowSubtaskForm(false);
      setEditingSubId(null);
      return;
    }

    if (!newSubtask.dueDate || (!newSubtask.isMultiDay && !newSubtask.time)) {
      setSubtaskError(t('validation.subtaskDateRequired'));
      return;
    }

    let start = new Date(`${newSubtask.dueDate}${newSubtask.time ? 'T'+newSubtask.time : ''}`);
    let end;
    if (newSubtask.isMultiDay) {
      if (!newSubtask.endDate) {
        setSubtaskError(t('validation.subtaskEndDateRequired'));
        return;
      }
      end = new Date(newSubtask.endDate);
    } else {
      end = new Date(start);
      end.setMinutes(end.getMinutes() + (newSubtask.duration || 0));
    }

    if (!formData.flexMode) {
      const parentStart = formData.dueDate ? new Date(formData.dueDate) : null;
      const parentEnd = formData.endDate ? new Date(formData.endDate) : null;
      if ((parentStart && start < parentStart) || (parentEnd && end > parentEnd)) {
        setSubtaskError(t('validation.subtaskOutOfRange'));
        return;
      }
      const overlaps = formData.subTasks.some(st => {
        if (!st.dueDate) return false;
        if (editingSubId && st.id === editingSubId) return false;
        const stStart = new Date(`${st.dueDate}${st.time ? 'T'+st.time : ''}`);
        const stEnd = st.endDate ? new Date(st.endDate) : new Date(stStart.getTime() + (st.duration || 0) * 60000);
        return stStart < end && start < stEnd;
      });
      if (overlaps) {
        setSubtaskError(t('validation.subtaskOverlap'));
        return;
      }
    }

    const subtask = {
      id: editingSubId || Date.now().toString(),
      title: newSubtask.title,
      dueDate: newSubtask.dueDate,
      endDate: newSubtask.isMultiDay ? newSubtask.endDate : undefined,
      time: newSubtask.isMultiDay ? null : newSubtask.time,
      duration: newSubtask.isMultiDay ? (end.getTime() - start.getTime()) / 60000 : newSubtask.duration,
      completed: newSubtask.completed || false,
      isMultiDay: newSubtask.isMultiDay || false
    };

    setFormData(prev => {
      const subs = editingSubId
        ? prev.subTasks.map(st => st.id === editingSubId ? subtask : st)
        : [...prev.subTasks, subtask];
      return {
        ...prev,
        subTasks: subs,
        duration: prev.flexMode ? subs.reduce((s, st) => s + (st.duration || 0), 0) : prev.duration
      };
    });

    setNewSubtask({ title: '', dueDate: '', endDate: '', time: '09:00', duration: 30, completed: false, isMultiDay: false });
    setShowSubtaskForm(false);
    setEditingSubId(null);
  };

  const handleRemoveSubtask = (subtaskId) => {
    setFormData(prev => {
      const subs = prev.subTasks.filter(st => st.id !== subtaskId);
      return {
        ...prev,
        subTasks: subs,
        duration: prev.flexMode ? subs.reduce((s, st) => s + (st.duration || 0), 0) : prev.duration
      };
    });
  };

  const handleEditSubtask = (sub) => {
    setNewSubtask(sub);
    setEditingSubId(sub.id);
    setShowSubtaskForm(true);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={task ? t('taskForm.editTask') : t('taskForm.createTask')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <SafeIcon icon={FiType} /> {t('taskForm.title')} *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder={t('taskForm.taskTitlePlaceholder')}
              className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white transition-colors ${
                errors.title
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-slate-200 dark:border-slate-600'
              }`}
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
          </div>

          {/* Project */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <SafeIcon icon={FiFolders} /> {t('taskForm.project')}
            </label>
            <select
              value={formData.projectId}
              onChange={(e) => handleChange('projectId', e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
            >
              <option value="default">{t('navigation.inbox')}</option>
              {projects.map(project => (
                project.id !== 'default' && (
                  <option key={project.id} value={project.id}>{project.name}</option>
                )
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <SafeIcon icon={FiFileText} /> {t('taskForm.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('taskForm.descriptionPlaceholder')}
              aria-describedby="description-help"
              rows={3}
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-slate-800 dark:text-white"
            />
            <p id="description-help" className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {t('taskForm.markdownSupported')}
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <SafeIcon icon={FiFlag} /> {t('taskForm.priority')}
            </label>
            <select
              value={formData.priority}
              onChange={(e) => handleChange('priority', e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
            >
              <option value="low">{t('taskForm.priorityLowFull')}</option>
              <option value="medium">{t('taskForm.priorityMediumFull')}</option>
              <option value="high">{t('taskForm.priorityHighFull')}</option>
            </select>
          </div>

          {/* Multi-day checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isMultiDay"
              checked={formData.isMultiDay}
              onChange={(e) => handleChange('isMultiDay', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-slate-100 border-slate-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600"
            />
            <label
              htmlFor="isMultiDay"
              className="ml-2 text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {t('taskForm.multiDayTask')}
            </label>
          </div>

          {/* Dates */}
          {!formData.isMultiDay && !formData.flexMode ? (
            // Single-day task (due date + time)
            <div className="space-y-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <SafeIcon icon={FiCalendar} /> {t('taskForm.dueDate')}
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleChange('dueDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white ${
                    errors.dueDate
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-slate-200 dark:border-slate-600'
                  }`}
                />
                {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
              </div>

              {formData.dueDate && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      <SafeIcon icon={FiClock} /> {t('taskForm.duration')}
                    </label>
                    <input
                      type="number"
                      min="5"
                      step="5"
                      value={formData.duration ?? ''}
                      onChange={(e) => handleChange('duration', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      <SafeIcon icon={FiClock} /> {t('taskForm.startTime')}
                    </label>
                    {formData.antiOverlap ? (
                      formData.duration ? (
                        <>
                          <select
                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white"
                            value={customStart ? 'custom' : formData.startTime}
                            onChange={(e) => {
                              if (e.target.value === 'custom') {
                                setCustomStart(true);
                                handleChange('startTime', '');
                              } else {
                                setCustomStart(false);
                                handleChange('startTime', e.target.value);
                              }
                            }}
                          >
                            <option value="">{t('taskForm.selectSlot')}</option>
                            {availableSlots.map(slot => (
                              <option
                                key={slot.time}
                                value={slot.time}
                                title={
                                  slot.highlight
                                    ? `${slot.highlight === 'end' ? 'Après' : 'Avant'} ${slot.taskName}`
                                    : undefined
                                }
                                className={
                                  slot.highlight === 'end'
                                    ? 'bg-green-100 dark:bg-green-900/40'
                                    : slot.highlight === 'start'
                                    ? 'bg-orange-100 dark:bg-orange-900/40'
                                    : ''
                                }
                              >
                                {slot.time}
                                {slot.highlight ? ' ℹ️' : ''}
                              </option>
                            ))}
                            <option value="custom">{t('taskForm.otherTime')}</option>
                          </select>
                          {customStart && (
                            <input
                              type="time"
                              value={formData.startTime}
                              onChange={(e) => handleChange('startTime', e.target.value)}
                              className="mt-2 w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                            />
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500">{t('taskForm.selectDurationFirst')}</p>
                      )
                    ) : (
                      <input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => handleChange('startTime', e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                      />
                    )}
                    {errors.startTime && <p className="text-red-500 text-sm mt-1">{errors.startTime}</p>}
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="antiOverlap"
                        checked={formData.antiOverlap}
                        onChange={(e) => handleChange('antiOverlap', e.target.checked)}
                        className="accent-green-500"
                      />
                      <label
                        htmlFor="antiOverlap"
                        className="text-sm text-green-600 flex items-center gap-1"
                        title={t('taskForm.antiOverlapDescription')}
                      >
                        {t('taskForm.antiOverlap')} <span>😊</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Multi-day task (start date + end date)
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <SafeIcon icon={FiCalendar} /> {t('taskForm.startDate')}
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleChange('dueDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white ${
                    errors.dueDate
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-slate-200 dark:border-slate-600'
                  }`}
                />
                {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <SafeIcon icon={FiCalendar} /> {t('taskForm.endDate')}
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  min={formData.dueDate || new Date().toISOString().split('T')[0]}
                  className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white ${
                    errors.endDate
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-slate-200 dark:border-slate-600'
                  }`}
                />
                {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <SafeIcon icon={FiTag} /> {t('taskForm.tags')}
            </label>
            <div className="flex gap-2 mb-2 flex-wrap">
              {formData.tags.map(tagId => {
                const tag = tags.find(tg => tg.id === tagId);
                return tag ? (
                  <div
                    key={tag.id}
                    className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-sm flex items-center gap-1"
                  >
                    <span>{tag.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag.id)}
                      className="hover:text-red-500 dark:hover:text-red-400"
                    >
                      <SafeIcon icon={FiX} className="text-xs" />
                    </button>
                  </div>
                ) : null;
              })}
            </div>
            {tags.length > 0 && (
              <div className="mb-3 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3">
                <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                  {t('taskForm.selectExistingTags')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => {
                    const selected = formData.tags.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleToggleTag(tag.id)}
                        className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                          selected
                            ? 'border-blue-500 bg-blue-600 text-white'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'
                        }`}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={t('taskForm.addTagPlaceholder')}
                className="flex-1 px-4 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-3 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} />
              </button>
            </div>
          </div>

          {/* Subtasks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                <SafeIcon icon={FiList} /> {t('taskForm.subtasks')}
              </label>
              <button
                type="button"
                onClick={() => setShowSubtaskForm(!showSubtaskForm)}
                className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                <SafeIcon icon={showSubtaskForm ? FiMinus : FiPlus} className="text-sm" />
              </button>
            </div>

            {/* Subtask List */}
            {formData.subTasks.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.subTasks.map(subtask => (
                  <div
                    key={subtask.id}
                    className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer"
                    onClick={() => handleEditSubtask(subtask)}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-800 dark:text-white">{subtask.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {subtask.dueDate
                          ? subtask.isMultiDay
                            ? `${subtask.dueDate}${subtask.endDate ? ` - ${subtask.endDate}` : ''}`
                            : `${subtask.dueDate} @ ${subtask.time}`
                          : t('taskForm.noDueDate')}
                        {subtask.duration ? ` (${subtask.duration} min)` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveSubtask(subtask.id); }}
                      className="p-1 text-slate-500 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {formData.subTasks.length >= 2 && !formData.isMultiDay && (
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.flexMode}
                  onChange={(e) => handleChange('flexMode', e.target.checked)}
                  className="accent-purple-500"
                  title={t('taskForm.flexModeDescription')}
                />
                <span className="text-sm font-medium text-purple-600">{t('taskForm.flexMode')}</span>
                {formData.flexMode && (
                  <span className="text-xs text-purple-500 ml-2">{t('taskForm.flexModeActivated')}</span>
                )}
              </div>
            )}

            {/* Add Subtask Form */}
            {showSubtaskForm && (
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 space-y-3 mb-2">
                <input
                  type="text"
                  value={newSubtask.title}
                  onChange={(e) => setNewSubtask({ ...newSubtask, title: e.target.value })}
                  placeholder={t('taskForm.subtaskPlaceholder')}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                />
                {(formData.isMultiDay || formData.flexMode) && (
                  <>
                    {formData.isMultiDay && (
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={newSubtask.isMultiDay}
                          onChange={(e) => setNewSubtask({ ...newSubtask, isMultiDay: e.target.checked })}
                          className="accent-blue-600"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{t('taskForm.multiDaySubtask')}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={newSubtask.dueDate}
                        onChange={(e) => setNewSubtask({ ...newSubtask, dueDate: e.target.value })}
                        min={formData.flexMode ? undefined : formData.dueDate || new Date().toISOString().split('T')[0]}
                        max={formData.flexMode ? undefined : formData.endDate || undefined}
                        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                        placeholder="Due date"
                      />
                      {!newSubtask.isMultiDay && (
                        <input
                          type="time"
                          value={newSubtask.time}
                          onChange={(e) => setNewSubtask({ ...newSubtask, time: e.target.value })}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                        />
                      )}
                      {newSubtask.isMultiDay && (
                        <input
                          type="date"
                          value={newSubtask.endDate}
                          onChange={(e) => setNewSubtask({ ...newSubtask, endDate: e.target.value })}
                          min={newSubtask.dueDate || undefined}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                          placeholder="End date"
                        />
                      )}
                    </div>
                  </>
                )}
                {!newSubtask.isMultiDay && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="5"
                      step="5"
                      value={newSubtask.duration}
                      onChange={(e) => setNewSubtask({ ...newSubtask, duration: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white"
                      placeholder="Duration (min)"
                    />
                    <button
                      type="button"
                      onClick={handleAddSubtask}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <SafeIcon icon={FiPlus} /> {t('taskForm.add')}
                    </button>
                  </div>
                )}
                {newSubtask.isMultiDay && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddSubtask}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <SafeIcon icon={FiPlus} /> {t('taskForm.add')}
                    </button>
                  </div>
                )}
                {subtaskError && (
                  <p className="text-red-500 text-sm">{subtaskError}</p>
                )}
              </div>
            )}
          </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
        >
          <SafeIcon icon={FiSave} />
          {task ? t('taskForm.updateTask') : t('taskForm.createTask')}
        </button>
      </form>
    </Modal>
  );
};

export default TaskForm;
